// frontend/src/pages/storeActivity/WebhooksPanel.jsx
//
// The updates Shopify and connected systems pushed to us for this store
// (backend core.models.WebhookEvent): per day, then per topic. Counted, not
// listed — a busy store receives thousands a month, which would bury the
// timeline. The topic stays its raw code: it is the name Shopify's and the
// connector's documentation use, and the one the admin filters by.

import React from 'react';
import { useTranslation } from 'react-i18next';
import { ICON, ImportIcon } from '../../icons';
import { useFormatters } from '../../hooks/useFormatters';
import { DailyBars, Panel, RelativeTime } from './primitives';

export default function WebhooksPanel({ webhooks, windowDays }) {
  const { t } = useTranslation();
  const { formatNumber } = useFormatters();
  const { days, topics } = webhooks;
  // The chart stacks disjoint counts; `received` includes the failed ones.
  const stacked = days.map((d) => ({ date: d.date, failed: d.failed, other: d.received - d.failed }));
  const total = topics.reduce((sum, topic) => sum + topic.received, 0);

  return (
    <Panel
      icon={<ImportIcon size={ICON.md} />}
      title={t('storeActivity.webhooks.title')}
      subtitle={t('storeActivity.webhooks.subtitle', { count: windowDays })}
    >
      {topics.length === 0
        ? <div className="sa-panel-empty">{t('storeActivity.webhooks.empty', { count: windowDays })}</div>
        : (
          <>
            <DailyBars
              days={stacked}
              label={t('storeActivity.webhooks.title')}
              series={[
                { key: 'failed', label: t('storeActivity.webhooks.failed'), modifier: 'failed' },
                { key: 'other', label: t('storeActivity.webhooks.other'), modifier: 'views' },
              ]}
            />
            <div className="sa-list">
              {topics.map((topic) => (
                <div className="sa-list-row" key={`${topic.vendor}:${topic.topic}`}>
                  <div className="sa-list-main">
                    <div className="sa-list-primary"><code className="sa-code">{topic.topic}</code></div>
                    <div className="sa-list-secondary">
                      {`${topic.vendor} · ${t('storeActivity.webhooks.received', { count: topic.received })}`}
                      {topic.failed > 0 && (
                        <span className="sa-badge sa-badge--warn">
                          {t('storeActivity.webhooks.failedCount', { count: topic.failed })}
                        </span>
                      )}
                    </div>
                  </div>
                  <RelativeTime className="sa-list-secondary" value={topic.last_received} />
                </div>
              ))}
            </div>
          </>
        )}
      {/* The total, so the reader need not add the bars up. */}
      {total > 0 && (
        <div className="sa-panel-note">
          {t('storeActivity.webhooks.total', { count: total, formatted: formatNumber(total, 0) })}
        </div>
      )}
    </Panel>
  );
}
